import {
  GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
  type DimensionCatalogEntry,
} from './metric-registry';
import type {
  CompileMetricQuerySpecInput,
  MetricFactDimensionColumn,
  MetricQueryCompileRefused,
  MetricQueryCompilerRefusalCode,
  MetricQueryDimensionFilterPlan,
} from './metric-query-compiler';

type MetricQueryArtifactFamily = 'governed_component_variance_artifact';

type ArtifactFamilyRequired = Readonly<{
  dimension: string;
  artifactFamily: MetricQueryArtifactFamily;
}>;

type ResolvedMetricQueryDimension =
  | Readonly<{ ok: true; kind: 'fact'; physicalColumn: MetricFactDimensionColumn }>
  | Readonly<{ ok: true; kind: 'artifact'; artifactFamily: MetricQueryArtifactFamily }>
  | MetricQueryCompileRefused;

type Refuse = (
  code: MetricQueryCompilerRefusalCode,
  message: string,
  details: Readonly<Record<string, unknown>>,
) => MetricQueryCompileRefused;

export type MetricQueryDimensionValidationResult =
  | Readonly<{
      ok: true;
      dimensionFilters: readonly MetricQueryDimensionFilterPlan[];
      artifactFamilyRequired?: ArtifactFamilyRequired;
    }>
  | MetricQueryCompileRefused;

const FACT_DIMENSION_COLUMNS = new Set<MetricFactDimensionColumn>([
  'businessLineKey',
  'geoKey',
  'customerKey',
  'laneKey',
  'siteKey',
  'glAccountKey',
  'functionKey',
  'subfunctionKey',
]);

const ARTIFACT_FAMILY_BY_PHYSICAL_COLUMN: ReadonlyMap<string, MetricQueryArtifactFamily> =
  new Map([['costPoolKey', 'governed_component_variance_artifact']]);

export function validateMetricQueryDimensions(
  input: CompileMetricQuerySpecInput,
  refuse: Refuse,
): MetricQueryDimensionValidationResult {
  const requestedDimensions = requestedDimensionSet(input);
  const requestedResult = validateRequestedDimensions(input, requestedDimensions, refuse);
  if (!requestedResult.ok) return requestedResult;

  const dimensionFilters: MetricQueryDimensionFilterPlan[] = [];
  for (const filter of input.spec.filters) {
    if (filter.dimension === 'period') {
      return refuse('unknown_dimension_member', 'Period is constrained through period window.', {
        dimension: filter.dimension,
      });
    }
    const resolved = resolveFactQueryDimension(
      filter.dimension,
      input.registry.getDimensionCatalog(filter.dimension),
      refuse,
    );
    if (!resolved.ok) return resolved;
    const knownMemberResult = validateKnownMembers(
      input,
      filter.dimension,
      filter.memberKeys,
      refuse,
    );
    if (knownMemberResult) return knownMemberResult;
    if (resolved.kind === 'fact') {
      dimensionFilters.push({
        dimension: filter.dimension,
        physicalColumn: resolved.physicalColumn,
        memberKeys: [...filter.memberKeys],
      });
    }
  }
  return {
    ok: true,
    dimensionFilters,
    artifactFamilyRequired: requestedResult.artifactFamilyRequired,
  };
}

function requestedDimensionSet(input: CompileMetricQuerySpecInput): ReadonlySet<string> {
  return new Set([
    ...input.spec.groupBy,
    ...input.spec.grain.level.map((level) => level.dimension),
    ...input.spec.filters.map((filter) => filter.dimension),
  ]);
}

function validateRequestedDimensions(
  input: CompileMetricQuerySpecInput,
  requestedDimensions: ReadonlySet<string>,
  refuse: Refuse,
):
  | Readonly<{ ok: true; artifactFamilyRequired?: ArtifactFamilyRequired }>
  | MetricQueryCompileRefused {
  let artifactFamilyRequired: ArtifactFamilyRequired | undefined;
  for (const dimension of requestedDimensions) {
    if (dimension === 'period') continue;
    const resolved = resolveFactQueryDimension(
      dimension,
      input.registry.getDimensionCatalog(dimension),
      refuse,
    );
    if (!resolved.ok) return resolved;
    if (resolved.kind === 'artifact') {
      artifactFamilyRequired ??= { dimension, artifactFamily: resolved.artifactFamily };
    }
  }
  return { ok: true, artifactFamilyRequired };
}

function validateKnownMembers(
  input: CompileMetricQuerySpecInput,
  dimension: string,
  memberKeys: readonly string[],
  refuse: Refuse,
): MetricQueryCompileRefused | null {
  const knownMembers = new Set(input.dimensionMembersByDimension[dimension] ?? []);
  for (const memberKey of memberKeys) {
    if (memberKey === GOVERNED_METRIC_FACT_ALL_MEMBER_KEY) continue;
    if (!knownMembers.has(memberKey)) {
      return refuse('unknown_dimension_member', 'Requested dimension member is not registered.', {
        dimension,
        memberKey,
      });
    }
  }
  return null;
}

function resolveFactQueryDimension(
  dimension: string,
  catalog: DimensionCatalogEntry | undefined,
  refuse: Refuse,
): ResolvedMetricQueryDimension {
  if (!catalog) {
    return refuse('unknown_dimension_member', 'Requested dimension is not registered.', {
      dimension,
    });
  }
  if (isFactDimensionColumn(catalog.physicalColumn)) {
    return { ok: true, kind: 'fact', physicalColumn: catalog.physicalColumn };
  }
  const artifactFamily = ARTIFACT_FAMILY_BY_PHYSICAL_COLUMN.get(catalog.physicalColumn);
  if (artifactFamily) return { ok: true, kind: 'artifact', artifactFamily };
  return refuse('unknown_dimension_member', 'Requested dimension is not queryable by facts.', {
    dimension,
    physicalColumn: catalog.physicalColumn,
  });
}

function isFactDimensionColumn(value: string): value is MetricFactDimensionColumn {
  return FACT_DIMENSION_COLUMNS.has(value as MetricFactDimensionColumn);
}
